import type { VisualEffect } from '../tilemap/types';

export class FireballEffect implements VisualEffect {
  private elapsed = 0;
  private readonly duration: number;
  private readonly fromX: number;
  private readonly fromY: number;
  private readonly toX: number;
  private readonly toY: number;
  private readonly tileToScreenCenter: (x: number, y: number) => { x: number; y: number };
  private readonly colors: {
    core: string;
    mid: string;
    outer: string;
    impactCore: string;
    impactMid: string;
    impactOuter: string;
  };

  constructor(options: {
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
    tileToScreenCenter: (x: number, y: number) => { x: number; y: number };
    colors?: {
      core: string;
      mid: string;
      outer: string;
      impactCore: string;
      impactMid: string;
      impactOuter: string;
    };
  }) {
    this.fromX = options.fromX;
    this.fromY = options.fromY;
    this.toX = options.toX;
    this.toY = options.toY;
    this.tileToScreenCenter = options.tileToScreenCenter;
    this.colors = options.colors ?? {
      core: 'rgba(254, 240, 138, 0.95)',
      mid: 'rgba(251, 146, 60, 0.95)',
      outer: 'rgba(239, 68, 68, 0)',
      impactCore: 'rgba(255, 255, 255, 0.9)',
      impactMid: 'rgba(251, 191, 36, 0.8)',
      impactOuter: 'rgba(239, 68, 68, 0)',
    };

    const dx = this.toX - this.fromX;
    const dy = this.toY - this.fromY;
    const tileDistance = Math.max(1, Math.hypot(dx, dy));
    this.duration = Math.max(0.16, Math.min(0.45, tileDistance * 0.08));
  }

  update(deltaSeconds: number): void {
    this.elapsed += deltaSeconds;
  }

  renderOverlay(ctx: CanvasRenderingContext2D): void {
    const progress = Math.min(this.elapsed / this.duration, 1);
    const eased = 1 - (1 - progress) * (1 - progress);

    const from = this.tileToScreenCenter(this.fromX, this.fromY);
    const to = this.tileToScreenCenter(this.toX, this.toY);
    const x = from.x + (to.x - from.x) * eased;
    const y = from.y + (to.y - from.y) * eased;

    ctx.save();

    const glow = ctx.createRadialGradient(x, y, 0, x, y, 12);
    glow.addColorStop(0, this.colors.core);
    glow.addColorStop(0.45, this.colors.mid);
    glow.addColorStop(1, this.colors.outer);
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(x, y, 12, 0, Math.PI * 2);
    ctx.fill();

    if (progress >= 0.98) {
      const impact = ctx.createRadialGradient(to.x, to.y, 0, to.x, to.y, 20);
      impact.addColorStop(0, this.colors.impactCore);
      impact.addColorStop(0.3, this.colors.impactMid);
      impact.addColorStop(1, this.colors.impactOuter);
      ctx.fillStyle = impact;
      ctx.beginPath();
      ctx.arc(to.x, to.y, 20, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isComplete(): boolean {
    return this.elapsed >= this.duration;
  }
}
